import { useCallback, useMemo, useState } from 'react';
import type { Key, SortOrder } from 'antd/es/table/interface';

import type { ApiKey } from '@/modules/api/codegen';

export type ApiKeysSortField = 'name' | 'createdAt' | 'lastUsedAt';

export interface ApiKeysSort {
    field: ApiKeysSortField;
    order: Exclude<SortOrder, null>;
}

const defaultSort: ApiKeysSort = { field: 'name', order: 'ascend' };
const defaultPageSize = 10;

// A key that has never been used sorts as the oldest — `Date.parse` would yield NaN, and a
// comparator returning NaN leaves the order undefined.
const timestamp = (value: string | null | undefined) => (value ? Date.parse(value) : 0);

const compare = (a: ApiKey, b: ApiKey, field: ApiKeysSortField) => {
    if (field === 'name') return a.name.localeCompare(b.name);

    return timestamp(a[field]) - timestamp(b[field]);
};

// `/api-keys` returns the caller's whole list at once and takes no query parameters, so searching,
// sorting and paging all happen here rather than on the server.
export const useApiKeysTableState = (apiKeys: ApiKey[] | undefined) => {
    const [search, setSearchValue] = useState('');
    const [sort, setSortValue] = useState(defaultSort);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const matchingKeys = useMemo(() => {
        const query = search.trim().toLowerCase();
        const matching = query ? (apiKeys ?? []).filter(apiKey => apiKey.name.toLowerCase().includes(query)) : (apiKeys ?? []);
        const direction = sort.order === 'ascend' ? 1 : -1;

        return [...matching].sort((a, b) => direction * compare(a, b, sort.field));
    }, [apiKeys, search, sort]);

    const rows = useMemo(() => matchingKeys.slice((page - 1) * pageSize, page * pageSize), [matchingKeys, page, pageSize]);

    const setSearch = useCallback((value: string) => {
        setSearchValue(value);
        setPage(1);
    }, []);

    const setSort = useCallback((field: ApiKeysSortField, order: SortOrder) => {
        setSortValue(order ? { field, order } : defaultSort);
        setPage(1);
    }, []);

    const setPagination = useCallback((nextPage: number, nextPageSize: number) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
    }, []);

    return {
        rows,
        total: matchingKeys.length,
        search,
        setSearch,
        sort,
        setSort,
        page,
        pageSize,
        setPagination,
        selectedRowKeys,
        setSelectedRowKeys,
    };
};
